/**
 * gauges.js — pure extraction of the SERVE and FLOW gauge math.
 *
 * The calculations below are copied VERBATIM (expression-for-expression) from
 * the computed properties inside StoreServiceGauge.vue and
 * StoreCapacityGauge.vue, as of 2026-06-09. They are pulled out here so:
 *   1. the Python signal layer can be parity-tested against a clean reference,
 *   2. (later, in 0b) the two gauge components can import these functions
 *      instead of duplicating the math — making this the single source.
 *
 * IMPORTANT: this module is NOT yet imported by the .vue components, so it does
 * not change any on-screen behaviour. When wiring it in (0b), diff carefully
 * against the .vue and verify the gauges render identically on the demo store.
 *
 * Pure functions, no Vue/DOM. Inputs mirror the component props.
 */
import {
  QUEUE_ELASTICITY,
  SERVICE_BANDS,
  CAPACITY_BANDS,
  CAPACITY_VALUE_SETTINGS,
  LADDER,
} from '@/prioritization/settings'

/**
 * The inverted four-rung WAIT ladder (docs/09, "protect" unit per docs/11).
 * Wait is lower-is-better, and the ladder speaks its NATIVE unit — TIME — only.
 * There is deliberately NO euro here: no single action "recovers" a precise wait
 * euro (unlike ATTRACT's within-reach), so attaching one would be a bluffed
 * number. The store-checkout "sales at risk" magnitude (a soft, ranged estimate)
 * lives on the SERVE gauge instead, never summed with measured euros.
 *  - headlinePct = own-best / current  (climbs to 100% as you approach your best)
 *  - peer / best-in-class ticks sit BEYOND 100% (faster than your own best)
 *  - active target = the nearest rung BELOW current → a wait reduction in MINUTES
 *
 * @param o { currentWaitSec, ownBestWaitSec?, peerWaitSec?, targetWaitSec? }
 */
export function computeWaitLadder(o) {
  const current = Number(o.currentWaitSec || 0)
  const rate = (x) => Number.isFinite(x) && x > 0
  const ownBest = rate(o.ownBestWaitSec) ? o.ownBestWaitSec : null
  const peer = rate(o.peerWaitSec) ? o.peerWaitSec : null
  const target = rate(o.targetWaitSec) ? o.targetWaitSec : null
  const anchor = ownBest != null ? ownBest : current // own-best anchors 100%

  const headlinePct = current > 0 ? Math.max(0, Math.min(100, (anchor / current) * 100)) : 0
  const peerTickPct = peer != null && current > 0 ? (anchor / peer) * 100 : null
  const ceilingTickPct = target != null && current > 0 ? (anchor / target) * 100 : null

  // Active = the nearest rung strictly below current (largest wait that still
  // improves), with a fractional hysteresis so a rung must beat current by MARGIN.
  const NAMES = { own: 'your best weeks', peer: 'your peers', ceil: 'best-in-class' }
  const rungs = [
    { key: 'own', name: NAMES.own, wait: ownBest },
    { key: 'peer', name: NAMES.peer, wait: peer },
    { key: 'ceil', name: NAMES.ceil, wait: target },
  ].filter((r) => r.wait != null && r.wait < current * (1 - LADDER.MARGIN))
  rungs.sort((a, b) => b.wait - a.wait) // nearest reduction first
  const active = rungs.length ? rungs[0] : null

  const atFloor = active == null
  const activeWaitSec = active ? active.wait : null
  const waitReductionSec = active ? Math.max(0, current - active.wait) : 0
  const waitReductionMin = waitReductionSec / 60

  return {
    currentWaitSec: current,
    ownBestWaitSec: ownBest,
    peerWaitSec: peer,
    targetWaitSec: target,
    headlinePct,
    peerTickPct,
    ceilingTickPct,
    atFloor,
    activeRung: active ? active.name : null,
    activeRungKey: active ? active.key : null,
    activeWaitSec,
    waitReductionSec,
    waitReductionMin,
  }
}

// computeStoreRealized — identical in both gauge components. Realized € used
// as the baseline the loss math scales. (No ceilings/overrides — just realized.)
function storeRealized(d) {
  if (!d) return 0
  const funnels = d.product_zone_funnels || {}
  const visitorsC = d.product_zone_visitors_counts || {}
  const clearC = d.product_zone_clear_lingerers_counts || {}
  const zones = new Set([
    ...Object.keys(funnels),
    ...Object.keys(visitorsC),
    ...Object.keys(clearC),
  ])
  let realizedTotal = 0
  zones.forEach((zone) => {
    const v = Number(visitorsC[zone] || 0)
    const clr = Number(clearC[zone] || 0)
    const avg = Number(funnels[zone]?.average_product_value || 0)
    const convRaw = Number(funnels[zone]?.conversion_rate || 0)
    const conversionRate = Math.min(1, Math.max(0, convRaw <= 1 ? convRaw : convRaw / 100))
    const engagementRate = v > 0 ? Math.min(1, Math.max(0, clr / v)) : 0
    realizedTotal += v * engagementRate * conversionRate * avg
  })
  return realizedTotal
}

/**
 * SERVE — Store Service Efficiency.
 * @param fixed       fixedWidgetsData
 * @param segmentKey  company sector
 * @param opts        { hasWaitingZones=true, isActive=true }
 */
export function computeServiceGauge(fixed, segmentKey = 'default', opts = {}) {
  const hasWaitingZones = opts.hasWaitingZones !== false
  const isActive = opts.isActive !== false
  const enabled = isActive && hasWaitingZones

  const cfg = QUEUE_ELASTICITY[segmentKey] || QUEUE_ELASTICITY.default

  const avgWaitSec = enabled ? Number(fixed?.average_waiting_time || 0) : 0
  const avgWaitMin = avgWaitSec / 60

  let targetWaitMin = 0
  if (enabled) {
    const backend = fixed?.target_wait_time_minutes
    targetWaitMin = backend != null && !isNaN(backend) ? backend : cfg.target_wait_min
  }
  const targetWaitSec = targetWaitMin * 60

  let pct = 0
  if (enabled && avgWaitSec && targetWaitSec) {
    pct = Math.max(0, Math.min(100, (targetWaitSec / avgWaitSec) * 100))
  }
  const pctRounded = Math.round(pct)

  const excessWaitMin = enabled ? Math.max(0, avgWaitMin - targetWaitMin) : 0

  let lossFraction = 0
  if (enabled && excessWaitMin > 0) {
    lossFraction = Math.min(Math.max(cfg.k_per_min * excessWaitMin, 0), cfg.max_loss)
  }
  const lossPct = Number((lossFraction * 100).toFixed(2))

  const baseRevenueEur = enabled ? storeRealized(fixed) : 0
  // "Sales AT RISK" (not lost, not recoverable) — a soft, explicitly-modelled
  // elasticity estimate of the checkout revenue exposed by waits above target.
  // docs/11: SERVE is "protect", its native unit is TIME; this euro is a ranged
  // (est.) RISK flag for Allen to rank alerts — never a headline, never summed
  // with measured euros, never presented as recoverable. Kept under the legacy
  // key `lostServiceEur` for back-compat, but `atRiskEst` marks its nature.
  const atRiskEur = Math.round(baseRevenueEur * lossFraction)
  const atRiskBand = LADDER.RANGE_PCT_EST
  const atRiskRange = atRiskEur > 0
    ? [Math.round(atRiskEur * (1 - atRiskBand)), Math.round(atRiskEur * (1 + atRiskBand))]
    : [0, 0]

  let status
  if (!hasWaitingZones) status = 'setup'
  else if (pct <= SERVICE_BANDS.CRITICAL) status = 'critical'
  else if (pct <= SERVICE_BANDS.WARNING) status = 'watch'
  else status = 'neutral'

  // Four-rung CLIMB for the store checkout wait (docs/09) — TIME only, no euro.
  // own-best (Rung 1) and the peer cohort p20 (Rung 3) arrive via opts; the
  // sector target is Rung 4. All optional — null-safe when absent.
  const climb =
    enabled && avgWaitSec > 0
      ? computeWaitLadder({
          currentWaitSec: avgWaitSec,
          ownBestWaitSec: opts.ownBestWaitSec ?? null,
          peerWaitSec: opts.cohortWaitSec ?? null,
          targetWaitSec,
        })
      : null

  return {
    enabled,
    avgWaitSec,
    avgWaitMin,
    targetWaitMin,
    targetWaitSec,
    pct,
    pctRounded,
    excessWaitMin,
    lossFraction,
    lossPct,
    baseRevenueEur,
    // Sales-at-risk (soft, ranged, modelled) — see note above.
    lostServiceEur: atRiskEur,
    atRiskEur,
    atRiskRange,
    atRiskEst: true,
    status,
    // four-rung climb (TIME native unit; null when disabled / no wait data)
    headlinePct: climb ? climb.headlinePct : null,
    peerTickPct: climb ? climb.peerTickPct : null,
    ceilingTickPct: climb ? climb.ceilingTickPct : null,
    ownBestWaitSec: climb ? climb.ownBestWaitSec : null,
    cohortWaitSec: climb ? climb.peerWaitSec : null,
    climb,
  }
}

function safePct(value) {
  const v = Number(value || 0)
  if (!Number.isFinite(v) || v < 0) return 0
  return v
}

// Capacity UTILISATION (docs/11): the share of paid capacity actually used.
// FLOW is a waste-only ("save") gauge — only UNDER-utilisation (overstaffing)
// scores below 100. Over-capacity (r ≥ 1) has no idle waste → capped at 100;
// the checkout straining on the over side belongs entirely to SERVE, so FLOW
// never "turns red" for it. (For r < 1 this equals the old balance score, so
// the overstaffed side is unchanged; only the removed over-capacity penalty
// differs — which is the point.)
function computeBalanceScore(r) {
  if (!Number.isFinite(r) || r <= 0) return 0
  return Math.min(100, Math.round(r * 100))
}

const MIN_USAGE_FOR_MEANING = 1.0

/**
 * FLOW — Store Capacity Balance.
 * @param fixed       fixedWidgetsData
 * @param segmentKey  company sector
 * @param opts        { isActive=true, hasCheckoutZones=true,
 *                      laneCountInteraction=0, laneCountCashier=0,
 *                      companyStaffCost=null }
 */
export function computeCapacityGauge(fixed, segmentKey = 'default', opts = {}) {
  const isActive = opts.isActive !== false
  const hasCheckoutZones = opts.hasCheckoutZones !== false
  const laneCountInteraction = Number(opts.laneCountInteraction || 0)
  const laneCountCashier = Number(opts.laneCountCashier || 0)
  const companyStaffCost = opts.companyStaffCost ?? null

  const segmentCfg = CAPACITY_VALUE_SETTINGS[segmentKey] || CAPACITY_VALUE_SETTINGS.default

  const interactionUsagePct = safePct(fixed?.checkout_interaction_usage_pct)
  const cashierUsagePct = safePct(fixed?.checkout_cashier_usage_pct)
  const loadFrac = interactionUsagePct / 100
  const capacityFrac = cashierUsagePct / 100

  const hasInteractionZones = laneCountInteraction > 0
  const hasCashierZones = laneCountCashier > 0
  const hasStructure = hasCheckoutZones && hasInteractionZones
  const hasInteractionUsage = hasStructure && Number.isFinite(interactionUsagePct) && interactionUsagePct > 0
  const hasCashierUsage = Number.isFinite(cashierUsagePct) && cashierUsagePct > 0
  const hasUsage = hasInteractionUsage && hasCashierUsage
  const isCardDisabled = !isActive || !hasStructure
  const isGaugeEnabled = !isCardDisabled && hasUsage
  const isLowActivity =
    interactionUsagePct < MIN_USAGE_FOR_MEANING && cashierUsagePct < MIN_USAGE_FOR_MEANING

  let ratio = null
  if (isGaugeEnabled) {
    const lanesFrac = capacityFrac
    if (lanesFrac && Number.isFinite(lanesFrac) && lanesFrac > 0) {
      const r = loadFrac / lanesFrac
      if (Number.isFinite(r) && r > 0) ratio = r
    }
  }

  const pct = isGaugeEnabled && ratio != null ? computeBalanceScore(ratio) : 0
  const pctRounded = Math.round(pct)

  // FLOW is one-directional (docs/11): under-utilisation (overstaffing) is the
  // only FLOW state with a cost; at/over capacity is "balanced" (no idle waste)
  // and any strain there is SERVE's story. `overloaded` is intentionally gone.
  let mode
  if (ratio == null) mode = 'setup'
  else if (pct >= 100) mode = 'balanced'
  else mode = 'overstaffed'

  const overrideCost = Number(companyStaffCost)
  let effectiveStaffCostPerHour = 0
  if (Number.isFinite(overrideCost) && overrideCost > 0) {
    effectiveStaffCostPerHour = overrideCost
  } else {
    const def = Number(segmentCfg.staff_cost_per_hour)
    effectiveStaffCostPerHour = Number.isFinite(def) && def > 0 ? def : 0
  }

  const openingHours = Number(fixed?.total_opening_hours || 0)
  const lanesForStaffing = laneCountCashier || laneCountInteraction || 0
  const staffedHours =
    !openingHours || !lanesForStaffing || !capacityFrac
      ? 0
      : openingHours * lanesForStaffing * capacityFrac

  const baseRevenueEur = isGaugeEnabled ? storeRealized(fixed) : 0

  let labourWasteHours = 0
  if (hasUsage && !isLowActivity && ratio && ratio < 1 && staffedHours) {
    const rawWaste = staffedHours * (1 - ratio)
    const maxWasteHours = staffedHours * (segmentCfg.max_labour_saving_fraction ?? 0.5)
    labourWasteHours = Math.min(rawWaste, maxWasteHours)
  }
  const labourWasteValue = labourWasteHours * effectiveStaffCostPerHour

  // FLOW's only cost is REAL: idle payroll you could redeploy (overstaffing =
  // hours × wage). The old bottleneck/throughput € (unmet demand × a made-up
  // alpha) is GONE — it double-counted the queue event SERVE already owns, so
  // one lost customer got summed twice. FLOW is now "save" only; the strain when
  // the till can't cope is entirely SERVE's story (docs/11).
  const costOfImbalance = labourWasteValue  // 0 unless overstaffed

  let status
  if (isCardDisabled) status = 'not_configured'
  else if (!hasUsage) status = 'setup'
  else if (isLowActivity) status = 'low_activity'
  else if (pctRounded <= CAPACITY_BANDS.CRITICAL) status = 'critical'
  else if (pctRounded <= CAPACITY_BANDS.WARNING) status = 'watch'
  else status = 'healthy'

  return {
    enabled: isGaugeEnabled,
    interactionUsagePct,
    cashierUsagePct,
    ratio,
    balanceScore: pct,
    balanceScoreRounded: pctRounded,
    mode,
    isLowActivity,
    effectiveStaffCostPerHour,
    staffedHours,
    baseRevenueEur,
    labourWasteHours,
    labourWasteValue,
    costOfImbalance,
    status,
  }
}
